import { ActionIcon, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconPlayerPause, IconPlayerPlay, IconX } from '@tabler/icons-react';
import { CookingTimer, timerRemainingMs, useCookingTimers, useStepFocus } from '../../contexts/CookingTimersContext';
import { formatCountdown } from '../../utils/stepDurations';
import classes from './CookingTimersTray.module.css';

// Apple's minimum recommended touch target; these get tapped mid-cooking.
const TOUCH_TARGET = 44;

/** "Step 3" from a "Step 3 · 30 min" label; the countdown already shows time. */
const stepLabel = (timer: CookingTimer) =>
  timer.origin ? `Step ${timer.origin.stepIndex + 1}` : timer.label.split(' · ')[0];

/**
 * Floating list of active step timers, bottom-left so it clears the
 * bottom-right FABs (contact, mobile ingredients). Rendered app-wide so
 * timers stay visible after leaving the recipe that started them.
 */
export const CookingTimersTray = () => {
  const ctx = useCookingTimers();
  const stepFocus = useStepFocus();
  const navigate = useNavigate();
  const location = useLocation();
  if (!ctx || ctx.timers.length === 0) return null;
  const { timers, now, pauseTimer, resumeTimer, addTime, dismissTimer } = ctx;

  // Return to the step that started the timer. The destination page reads
  // the focus request on mount (or immediately, if it's already showing).
  const goToStep = (timer: CookingTimer) => {
    const o = timer.origin;
    if (!o) return;
    stepFocus?.requestStepFocus(o.recipeId, o.stepIndex);
    if (location.pathname !== o.path) navigate(o.path, { state: o.state });
  };

  return (
    <Stack
      gap={8}
      role="region"
      aria-label="Cooking timers"
      style={{
        position: 'fixed',
        // Mirrors the mobile ingredients FAB (24px inset, 60px tall) so the
        // two sit on the same baseline. Pages with a bottom banner publish
        // --floating-controls-bottom (see RecipeDetailsView) to lift both.
        left: 24,
        bottom: 'var(--floating-controls-bottom, 24px)',
        transition: 'bottom 200ms ease',
        zIndex: 200,
        width: 300,
        // Leave room for the right-side floating buttons on phones
        // (24px inset + 60px FAB + 12px gap, plus our own 24px inset).
        maxWidth: 'calc(100vw - 120px)',
      }}
    >
      {timers.map((timer) => {
        const done = timer.status === 'done';
        const running = timer.status === 'running';
        return (
          <Paper
            key={timer.id}
            shadow="md"
            radius="md"
            px="sm"
            // 44px buttons + 2×8px padding = the FAB's 60px (verified in Chromium).
            py={8}
            withBorder
            className={done ? classes.done : undefined}
            style={{
              borderColor: done ? 'var(--mantine-color-orange-5)' : undefined,
              // The light tint is translucent; layer it over an opaque base.
              background: done
                ? 'linear-gradient(var(--mantine-color-orange-light), var(--mantine-color-orange-light)), var(--mantine-color-body)'
                : 'var(--mantine-color-body)',
            }}
          >
            <Group gap={8} wrap="nowrap">
              <UnstyledButton
                onClick={() => goToStep(timer)}
                disabled={!timer.origin}
                aria-label={timer.origin ? `Go to step ${timer.origin.stepIndex + 1}${timer.recipeName ? ` of ${timer.recipeName}` : ''}` : undefined}
                style={{ flex: 1, minWidth: 0, alignSelf: 'stretch', display: 'flex', alignItems: 'center', cursor: timer.origin ? 'pointer' : 'default' }}
              >
                <Stack gap={0} style={{ minWidth: 0 }}>
                  {/* The recipe name gets its own line: the tray is narrow on
                      phones (it clears the right-side FABs), and sharing the
                      line with the step truncated it to a few characters. */}
                  {timer.recipeName && (
                    <Text size="xs" c="dimmed" truncate>{timer.recipeName}</Text>
                  )}
                  <Group gap={6} wrap="nowrap" align="baseline">
                    <Text
                      fw={700}
                      size="lg"
                      c={done ? 'orange.8' : undefined}
                      style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2, whiteSpace: 'nowrap' }}
                    >
                      {done ? 'Done' : formatCountdown(timerRemainingMs(timer, now))}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>{stepLabel(timer)}</Text>
                  </Group>
                </Stack>
              </UnstyledButton>
              {!done && (
                <ActionIcon
                  variant="light"
                  color="teal"
                  size={TOUCH_TARGET}
                  radius="md"
                  onClick={() => (running ? pauseTimer(timer.id) : resumeTimer(timer.id))}
                  aria-label={running ? 'Pause timer' : 'Resume timer'}
                >
                  {running ? <IconPlayerPause size={22} /> : <IconPlayerPlay size={22} />}
                </ActionIcon>
              )}
              {/* +1m only once finished ("just one more minute"), so at
                  most two buttons compete with the label for width. */}
              {done && (
                <ActionIcon
                  variant="light"
                  color="orange"
                  onClick={() => addTime(timer.id, 60)}
                  aria-label="Add one minute"
                  size={TOUCH_TARGET}
                  radius="md"
                  w="auto"
                  miw={TOUCH_TARGET + 8}
                  px={8}
                >
                  <Text size="sm" fw={700}>+1m</Text>
                </ActionIcon>
              )}
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => dismissTimer(timer.id)}
                aria-label="Dismiss timer"
                size={TOUCH_TARGET}
                radius="md"
              >
                <IconX size={22} />
              </ActionIcon>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
};

